
import { supabase } from "@/integrations/supabase/client";
import { ReagentRequest } from "@/types/reagentRequest.types";
import { enhanceRequestWithRelations } from "./requestsBaseService";
import { createLogger } from "@/services/utils/loggingUtils";

const logger = createLogger("FetchRequestsService");

// Fetch all reagent requests (not fulfilled or deleted)
export const fetchReagentRequests = async (): Promise<ReagentRequest[]> => {
  try {
    // First, fetch the basic reagent requests data
    const { data, error } = await supabase
      .from("reagent_requests")
      .select("*")
      .eq("is_fulfilled", false)
      .eq("is_deleted", false)
      .order("created_at", { ascending: false });

    if (error) {
      logger.error("Error fetching reagent requests:", error);
      throw error;
    }

    // Enhance each request with profile and category information
    const enhancedRequests = await Promise.all(
      data.map(request => enhanceRequestWithRelations(request))
    );

    // Filter out requests where the profile is null (pending/deleted users)
    const validRequests = enhancedRequests.filter(request => request.profiles !== null);

    logger.info("Fetched and enhanced reagent requests:", validRequests.length);
    return validRequests as ReagentRequest[];
  } catch (error) {
    logger.error("Exception in fetchReagentRequests:", error);
    return [];
  }
};

// Fetch reagent requests by user ID
export const fetchUserReagentRequests = async (userId: string): Promise<ReagentRequest[]> => {
  try {
    // First, fetch the basic reagent requests data
    const { data, error } = await supabase
      .from("reagent_requests")
      .select("*")
      .eq("user_id", userId)
      .eq("is_deleted", false)
      .order("created_at", { ascending: false });

    if (error) {
      logger.error("Error fetching user reagent requests:", error);
      throw error;
    }

    // Enhance each request with profile and category information
    const enhancedRequests = await Promise.all(
      data.map(request => enhanceRequestWithRelations(request))
    );

    // Filter out requests where the profile is null (pending/deleted users)
    const validRequests = enhancedRequests.filter(request => request.profiles !== null);

    return validRequests as ReagentRequest[];
  } catch (error) {
    logger.error("Exception in fetchUserReagentRequests:", error);
    return [];
  }
};

// Fetch reagent requests created within the last 24 hours
export const fetchNewReagentRequests = async (): Promise<ReagentRequest[]> => {
  try {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    
    // First, fetch the basic reagent requests data
    const { data, error } = await supabase
      .from("reagent_requests")
      .select("*")
      .eq("is_fulfilled", false)
      .eq("is_deleted", false)
      .gte("created_at", oneDayAgo.toISOString())
      .order("created_at", { ascending: false });

    if (error) {
      logger.error("Error fetching new reagent requests:", error);
      throw error;
    }

    // Enhance each request with profile and category information
    const enhancedRequests = await Promise.all(
      data.map(request => enhanceRequestWithRelations(request))
    );

    // Filter out requests where the profile is null (pending/deleted users)
    const validRequests = enhancedRequests.filter(request => request.profiles !== null);

    return validRequests as ReagentRequest[];
  } catch (error) {
    logger.error("Exception in fetchNewReagentRequests:", error);
    return [];
  }
};
